import type { SessionUser } from '@accounts/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { STR } from '../i18n';
import { ApiError, changeOwnPassword } from '../lib/api';
import { queryKeys } from '../lib/queryKeys';
import { landingPath } from '../lib/surfaces';
import { BrandPanel } from '../ui/BrandPanel';
import { Spinner } from '../ui/Primitives';

const MIN_NEW_PASSWORD_LENGTH = 12;

/**
 * THE MUST-CHANGE-PASSWORD WALL.
 *
 * Reachable and usable while walled because it depends only on POST /api/auth/me/password,
 * which stays open for a walled session; every other authenticated endpoint 403s
 * `password_change_required` until this is done. The client-side length and match checks
 * are affordance only — the server validates independently and is the source of truth.
 *
 * A PENDING DESTINATION SURVIVES THE WALL. Whatever `?redirect=` the guard (or the sign-in
 * page) carried here travels back to the server with the password change, which re-validates
 * it from scratch against the live allow-list — the client never decides that a redirect is
 * legitimate, and only ever honours the `redirectTo` that comes back.
 */
export function ChangePasswordPage({ user }: { user: SessionUser }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const redirectParam = searchParams.get('redirect');
  const redirect = redirectParam === null || redirectParam === '' ? undefined : redirectParam;

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [invalidField, setInvalidField] = useState<'new' | 'confirm' | null>(null);

  const changePasswordMutation = useMutation({
    mutationFn: () =>
      changeOwnPassword(
        redirect === undefined
          ? { currentPassword, newPassword }
          : { currentPassword, newPassword, redirect },
      ),
    onSuccess: async (result) => {
      setFormError(null);
      setInvalidField(null);
      await queryClient.invalidateQueries({ queryKey: queryKeys.me });

      // The SERVER's verdict, never the query parameter this page arrived with. Non-null is
      // always an absolute URL to a registered active application; null means "land inside
      // Accounts", and which surface that is comes from the one route declaration — the same
      // one for everybody, so nothing here needs to know who just changed their password.
      if (result.redirectTo !== null) {
        window.location.assign(result.redirectTo);
        return;
      }
      navigate(landingPath(), { replace: true });
    },
    onError: (error) => {
      setInvalidField(null);
      setFormError(error instanceof ApiError ? error.message : STR.errors.generic);
    },
  });

  const busy = changePasswordMutation.isPending;

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (newPassword.length < MIN_NEW_PASSWORD_LENGTH) {
      setInvalidField('new');
      setFormError(STR.changePassword.tooShort);
      return;
    }
    if (newPassword !== confirmPassword) {
      setInvalidField('confirm');
      setFormError(STR.changePassword.mismatch);
      return;
    }

    setInvalidField(null);
    setFormError(null);
    changePasswordMutation.mutate();
  }

  function clearError() {
    if (formError !== null) {
      setFormError(null);
      setInvalidField(null);
    }
  }

  return (
    <BrandPanel>
      <div data-view="change">
        <h1 className="f-title">{STR.changePassword.title}</h1>
        <p className="lead">{STR.changePassword.lead}</p>
        <p className="who">{user.email}</p>
        <div className="stack">
          {formError === null ? null : (
            <div className="msg call-red" role="alert">
              {formError}
            </div>
          )}
          <form onSubmit={handleSubmit} noValidate>
            <div className="stack">
              <label className="lbl" htmlFor="current-password">
                <span>{STR.changePassword.currentPasswordLabel}</span>
                <input
                  className="field"
                  id="current-password"
                  type="password"
                  autoComplete="current-password"
                  disabled={busy}
                  value={currentPassword}
                  onChange={(event) => {
                    setCurrentPassword(event.target.value);
                    clearError();
                  }}
                />
              </label>
              <label className="lbl" htmlFor="new-password">
                <span>{STR.changePassword.newPasswordLabel}</span>
                <input
                  className={invalidField === 'new' ? 'field field-over' : 'field'}
                  id="new-password"
                  type="password"
                  autoComplete="new-password"
                  disabled={busy}
                  value={newPassword}
                  onChange={(event) => {
                    setNewPassword(event.target.value);
                    clearError();
                  }}
                />
                <span className="hint">{STR.changePassword.lengthHint}</span>
              </label>
              <label className="lbl" htmlFor="confirm-password">
                <span>{STR.changePassword.confirmPasswordLabel}</span>
                <input
                  className={invalidField === 'confirm' ? 'field field-over' : 'field'}
                  id="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  disabled={busy}
                  value={confirmPassword}
                  onChange={(event) => {
                    setConfirmPassword(event.target.value);
                    clearError();
                  }}
                />
              </label>
              <div className="actions">
                <button className="prim btn btn-main" type="submit" disabled={busy} aria-busy={busy}>
                  {busy ? <Spinner /> : null}
                  <span>{busy ? STR.changePassword.submitting : STR.changePassword.submit}</span>
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </BrandPanel>
  );
}
